import { useEffect, useState } from "react";
import { Search, History } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage, typeLabel } from "./hierarchy";
import { ClassBadge, DeviceDetail, TransitionBadge } from "./viz";

const TYPE_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "porta_isca", label: "Porta-isca" },
  { value: "trex", label: "T-REX" },
  { value: "armadilha_luminosa", label: "Armadilha luminosa" },
  { value: "armadilha_biologica", label: "Armadilha biológica" },
  { value: "armadilha_mecanica", label: "Armadilha mecânica" },
  { value: "placa_cola", label: "Placa de cola" },
  { value: "catrex", label: "Catrex" },
  { value: "outro", label: "Outro" },
];

const PERIOD_OPTIONS = [
  { value: "", label: "Todo o período" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
  { value: "90d", label: "Últimos 90 dias" },
  { value: "12m", label: "Últimos 12 meses" },
];

export default function HistoryPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [typeKey, setTypeKey] = useState("");
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 50, period };
      if (q) {
        params.q = q;
      }
      if (typeKey) {
        params.type_key = typeKey;
      }
      const r = await api.get("/device-monitoring/monitorings/v2", {
        params,
      });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setRows([]);
      setTotal(0);
      toast.error(apiErrorMessage(err, "Erro ao carregar histórico."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeKey, period]);

  const search = (e) => {
    e.preventDefault();
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h3 className="font-semibold">
          Histórico de Monitoramentos ({total})
        </h3>
        <form onSubmit={search} className="flex items-center gap-2 flex-wrap">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 rounded-md border border-border bg-background text-sm"
          >
            {PERIOD_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <select
            value={typeKey}
            onChange={(e) => setTypeKey(e.target.value)}
            className="px-3 py-2 rounded-md border border-border bg-background text-sm"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar dispositivo..."
              className="pl-10 pr-4 py-2 rounded-md border border-border bg-background text-sm w-64"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium"
          >
            Buscar
          </button>
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-muted-foreground">
                  <th className="text-left py-3 px-4">Data</th>
                  <th className="text-left py-3 px-4">Dispositivo</th>
                  <th className="text-left py-3 px-4">Tipo</th>
                  <th className="text-left py-3 px-4">Área</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Classificação</th>
                  <th className="text-left py-3 px-4">Transição</th>
                  <th className="text-left py-3 px-4">Reincidência</th>
                  <th className="text-left py-3 px-4">Técnico</th>
                  <th className="text-left py-3 px-4">Cronograma</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/50">
                    <td className="py-3 px-4 whitespace-nowrap">
                      {r.device_date ? new Date(`${r.device_date}T12:00:00`).toLocaleDateString("pt-BR") : "—"}
                    </td>
                    <td className="py-3 px-4 font-medium">
                      <button className="underline font-mono" onClick={() => setDevice(r.device_id)}>
                        {r.device_code || "—"}
                      </button>
                    </td>
                    <td className="py-3 px-4">{typeLabel(r.type_key)}</td>
                    <td className="py-3 px-4">{r.area_name || "—"}</td>
                    <td className="py-3 px-4">
                      {(r.result_status || r.status || "—").replace(/_/g, " ")}
                      {r.total_weight_g ? ` · ${r.total_weight_g} g` : r.capture ? ` · ${r.total ?? 0}` : ""}
                      {r.raw_status && r.raw_status !== r.result_status && (
                        <span className="block text-xs text-muted-foreground">enviado: {r.raw_status}</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <ClassBadge cls={r.classification?.class} label={r.classification?.label} reason={r.classification?.reason} />
                    </td>
                    <td className="py-3 px-4">
                      <TransitionBadge dir={r.transition_direction} text={r.transition} />
                      {r.transition && <span className="block text-xs text-muted-foreground">{r.transition}</span>}
                    </td>
                    <td className="py-3 px-4 text-xs">{r.recurrence?.label || "—"}</td>
                    <td className="py-3 px-4">{r.technician_name || "—"}</td>
                    <td className="py-3 px-4 text-xs text-muted-foreground font-mono">
                      {r.schedule_id ? r.schedule_id.slice(0, 8) : "—"}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="py-8 text-center text-muted-foreground"
                    >
                      Nenhum registro encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {device && <DeviceDetail deviceId={device} onClose={() => setDevice(null)} />}
    </div>
  );
}
